import { pick } from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';

import OfferDetails, { IOfferDetailsProps } from '../../components/brokerageOfferFlow/OfferDetails';
import { IBrokerageInfo } from '../../declarations';
import { brokerageOfferPath } from '../../routes/pathBuilders';
import * as brokerageOfferFlowActions from '../../store/brokerageOfferFlow/actions';
import * as brokerageOfferFlowSelectors from '../../store/brokerageOfferFlow/selectors';
import OfferLayout, { IProps as IOfferLayoutProps } from './OfferLayout';

type IOfferDetailsPageProps = IOfferLayoutProps &
  IOfferDetailsProps & {
    beginOfferAccept: () => void;
    hasDeclineReason: boolean;
    isAccepted: boolean;
    redirectToComplete: () => void;
    showDeclineOfferModal: () => void;
  };

const OfferDetailsPage: React.SFC<IOfferDetailsPageProps> = props => {
  const {
    beginOfferAccept,
    brokerageInfo,
    match,
    hasDeclineReason,
    isAccepted,
    isExpired,
    redirectToComplete,
    showDeclineOfferModal,
  } = props;
  const prevUrl = brokerageOfferPath(match.params.token, 'price');
  const canAccept = !isExpired && !hasDeclineReason;
  const canDecline = !isAccepted && !isExpired && !hasDeclineReason;
  const primaryBtnText = canAccept && (isAccepted ? 'Next' : 'Next: Purchase agreement');
  const secondaryBtnText = canDecline && 'Decline offer';

  return (
    <OfferLayout
      disclaimerType="trusted-buyer"
      headerType="BROKERAGE"
      footerType="BROKERAGE"
      brokerageInfo={brokerageInfo}
      prevBtnUrl={prevUrl}
      prevBtnText={'Back'}
      onPrimaryBtnClick={isAccepted ? redirectToComplete : beginOfferAccept}
      primaryBtnText={primaryBtnText}
      secondaryBtnText={secondaryBtnText}
      onSecondaryBtnClick={showDeclineOfferModal}
    >
      <OfferDetails {...props} />
    </OfferLayout>
  );
};

const mapStateToProps = createSelector(
  brokerageOfferFlowSelectors.offerDetails,
  brokerageOfferFlowSelectors.hasOdOffer,
  brokerageOfferFlowSelectors.brokerageOtherClosingCostsCents,
  brokerageOfferFlowSelectors.odOtherClosingCostsCents,
  brokerageOfferFlowSelectors.brokerageTotalCostCents,
  brokerageOfferFlowSelectors.odTotalCostCents,
  brokerageOfferFlowSelectors.brokerageInfo,
  brokerageOfferFlowSelectors.brokerageOfferHeadlineCents,
  brokerageOfferFlowSelectors.hasDeclineReason,
  brokerageOfferFlowSelectors.isShowingDeclineModal,
  brokerageOfferFlowSelectors.isAccepted,
  brokerageOfferFlowSelectors.isExpired,
  (
    offerDetails,
    hasOdOffer: boolean,
    brokerageOtherClosingCostsCents: number,
    odOtherClosingCostsCents: number,
    brokerageTotalCostCents: number,
    odTotalCostCents: number,
    brokerageInfo: IBrokerageInfo,
    brokerageOfferHeadlineCents: number,
    hasDeclineReason: boolean,
    isShowingDeclineModal: boolean,
    isAccepted: boolean,
    isExpired,
  ): Partial<IOfferDetailsPageProps> => {
    const jsOfferDetails = offerDetails.toJS();

    const {
      brokerageOfferExpirationDate,
      brokerageBrokerFees,
      streetAddress,
      titleInsuranceEscrowNotaryCents,
      odAvgNumShowings,
      odEstDaysBuyer,
    } = jsOfferDetails;

    const calculatorState = pick(
      jsOfferDetails,
      'hoaTransferFeeCents',
      'homeOwnershipOverlapCosts',
      'mortgagePayoffCents',
      'odBrokerFees',
      'odHomeOwnership',
      'odOfferHeadlineCents',
      'odSellerConcessions',
      'repairsNeeded',
      'sellerConcessions',
      'showSubFields',
    );

    // Fall back on using REIT headline price for Traditional home sale if we don't have an OD offer.
    const odOfferHeadlineCents = calculatorState['odOfferHeadlineCents'];

    // total for currency percents to base off; this is to avoid dividing by 0
    const odOfferHeadlineCentsRefPoint = odOfferHeadlineCents || Infinity;

    const brokerageTotalCents = Math.max(
      brokerageOfferHeadlineCents - brokerageTotalCostCents || 0,
      0,
    );

    const odTotalCents = Math.max(odOfferHeadlineCents - odTotalCostCents || 0, 0);

    const calculatorFields = [
      {
        key: 'header',
        type: 'HEADER' as 'HEADER',
      },
      {
        key: 'sellingProcessSubheader',
        label: 'Selling process',
        type: 'SUBHEADER' as 'SUBHEADER',
      },
      {
        key: 'estDaysBuyer',
        label: 'Estimated days to find a buyer',
        tooltip:
          'This is the estimated number of days your home would be on market before it \
                  sells. We base this off similar home listings in your neighborhood.',
        type: 'HEADLINE_FIELD' as 'HEADLINE_FIELD',
      },
      {
        key: 'avgNumShowings',
        label: 'Average number of showings',
        tooltip: 'This is the estimated number of showings you will need to receive an offer.',
        type: 'HEADLINE_FIELD' as 'HEADLINE_FIELD',
      },
      {
        key: 'transactionCostsSubheader',
        label: 'Transaction costs',
        type: 'SUBHEADER' as 'SUBHEADER',
      },
      {
        key: 'headlinePrice',
        label: 'Offer/Contract price',
        tooltip: (
          <div>
            <h6 className="text-small">Sell to an Opendoor Trusted Buyer</h6>
            <p>
              We reached out to our partner network to collect offers on your home. This was the
              highest offer one of our partners made, and higher than the Opendoor Offer amount our
              valuation professionals calculated based on hundreds of data points and the
              information you provided.
            </p>
            <br />
            <h6 className="text-small">Traditional home sale</h6>
            {hasOdOffer ? (
              <p>
                To give you a comparison point, we've used the price that Opendoor could have
                offered for your home based on its valuation model. But feel free to adjust this to
                what you or your realtor think you could get for your home.
              </p>
            ) : (
              <p>
                We've inputted your Trusted Buyer offer price to get you started, but feel free to
                adjust this to what you or your realtor think you could get for your home.
              </p>
            )}
          </div>
        ),
        type: 'FIELD' as 'FIELD',
      },
      {
        key: 'brokerFees',
        label: 'Broker fees',
        tooltip: (
          <div>
            <h6 className="text-small">Sell to an Opendoor Trusted Buyer</h6>
            <p>
              There is a total brokerage fee of {brokerageBrokerFees.percent}% for this transaction,
              which you can compare to the total listing and buyer agent fees in a traditional sale.
            </p>
            <br />
            <h6 className="text-small">Traditional home sale</h6>
            <p>
              In a traditional sale, listing and buyer agent commission fees often total 5-6% of the
              purchase price, but it varies by home. You can enter a custom percentage.
            </p>
          </div>
        ),
        type: 'FIELD' as 'FIELD',
      },
      {
        key: 'repairsNeeded',
        label: 'Repairs needed to sell home',
        type: 'FIELD' as 'FIELD',
      },
      {
        isEditable: true,
        key: 'otherClosingCosts',
        label: 'Other closing costs',
        onUpdateKey: 'showSubFields',
        subFields: [
          {
            key: 'sellerConcessions',
            label: 'Seller concessions',
            tooltip:
              "In a traditional sale, buyers may ask sellers to pay costs on their behalf, such \
                      as inspection fees, transfer taxes, and more. When requested, these costs often \
                      add up to 1.5-2% of the home's price, but you can enter a custom percentage.",
            type: 'FIELD' as 'FIELD',
          },
          {
            key: 'titleEscrow',
            label: 'Title, escrow, notary, and transfer tax',
            tooltip:
              "In any sale, there will be fees for title insurance, escrow, recording & \
                      notarization, and real property transfer taxes often totaling between 1-3% of \
                      the home's price. Since this varies by home, you can enter a \
                      custom percentage.",
            type: 'FIELD' as 'FIELD',
          },
          {
            key: 'homeOwnershipOverlapCosts',
            label: 'Home ownership and overlap costs',
            tooltip:
              "This is an estimate of the additional costs you may pay for owning your home for \
                      a longer period of time, including taxes, insurance, utilities, HOA fees, and \
                      costs related to overlapping mortgages if your home sale and new property don't \
                      close at the same time.",
            type: 'FIELD' as 'FIELD',
          },
          {
            key: 'mortgagePayoffCents',
            label: 'Mortgage payoff',
            type: 'FIELD' as 'FIELD',
          },
          {
            key: 'hoaTransferFeeCents',
            label: 'HOA transfer fee',
            tooltip:
              'In any sale, if your current home is part of an HOA, you will likely have to pay \
                      a fee to transfer membership to the new owner. The average transfer fee is $250, \
                      but you can enter a custom amount.',
            type: 'FIELD' as 'FIELD',
          },
        ],
        type: 'SUBFIELD' as 'SUBFIELD',
      },
      {
        key: 'totalSubheader',
        type: 'SUBHEADER' as 'SUBHEADER',
      },
      {
        key: 'total',
        label: 'Net proceeds',
        type: 'FIELD' as 'FIELD',
      },
      {
        key: 'checkpoints',
        type: 'FIELD' as 'FIELD',
      },
    ];

    const brokerageColumn = {
      avgNumShowings: {
        config: {
          bold: true,
          children: '0 showings',
          primary: true,
        },
        type: 'TEXT' as 'TEXT',
      },
      brokerFees: {
        config: {
          total: brokerageOfferHeadlineCents,
          ...brokerageBrokerFees,
        },
        type: 'CURRENCY_PERCENT' as 'CURRENCY_PERCENT',
      },
      checkpoints: {
        config: {
          isPerk: true,
          perks: ['Flexible close date', 'Skip the home showings', 'Cancel with no penalty'],
        },
        type: 'PERK_LIST' as 'PERK_LIST',
      },
      estDaysBuyer: {
        config: {
          bold: true,
          children: '0 days',
          primary: true,
        },
        type: 'TEXT' as 'TEXT',
      },
      header: {
        config: {
          headline: brokerageOfferHeadlineCents,
          primary: true,
          subtitle: `Offer expires ${brokerageOfferExpirationDate}`,
          title: 'Sell to an Opendoor Trusted Buyer',
        },
        type: 'HEADER' as 'HEADER',
      },
      headlinePrice: {
        config: {
          value: brokerageOfferHeadlineCents,
        },
        type: 'CURRENCY' as 'CURRENCY',
      },
      hoaTransferFeeCents: {
        isEditable: true,
        onUpdateKey: 'hoaTransferFeeCents',
        type: 'CURRENCY' as 'CURRENCY',
      },
      homeOwnershipOverlapCosts: {
        config: {
          value: 0,
        },
        type: 'CURRENCY' as 'CURRENCY',
      },
      mortgagePayoffCents: {
        isEditable: true,
        onUpdateKey: 'mortgagePayoffCents',
        type: 'CURRENCY' as 'CURRENCY',
      },
      otherClosingCosts: {
        config: {
          percent: +((brokerageOtherClosingCostsCents * 100) / brokerageOfferHeadlineCents).toFixed(
            2,
          ),
          total: brokerageOfferHeadlineCents,
          value: brokerageOtherClosingCostsCents,
        },
        type: 'CURRENCY_PERCENT' as 'CURRENCY_PERCENT',
      },
      repairsNeeded: {
        config: {
          placeholder: 'TBD',
        },
        isEditable: true,
        onUpdateKey: 'repairsNeeded',
        type: 'CURRENCY' as 'CURRENCY',
      },
      sellerConcessions: {
        config: {
          value: 0,
        },
        type: 'CURRENCY' as 'CURRENCY',
      },
      titleEscrow: {
        config: {
          value: titleInsuranceEscrowNotaryCents,
        },
        type: 'CURRENCY' as 'CURRENCY',
      },
      total: {
        config: {
          bolder: true,
          value: brokerageTotalCents,
        },
        type: 'CURRENCY' as 'CURRENCY',
      },
    };

    const odColumn = {
      avgNumShowings: {
        config: {
          bold: true,
          children: `${odAvgNumShowings} showings`,
        },
        type: 'TEXT' as 'TEXT',
      },
      brokerFees: {
        config: {
          total: odOfferHeadlineCentsRefPoint,
        },
        isEditable: true,
        onUpdateKey: 'odBrokerFees',
        type: 'CURRENCY_PERCENT' as 'CURRENCY_PERCENT',
      },
      checkpoints: {
        config: {
          isPerk: false,
          perks: [
            'Unpredictable close date',
            'Inconvenient showings',
            'Potential cancellation penalties',
          ],
        },
        type: 'PERK_LIST' as 'PERK_LIST',
      },
      estDaysBuyer: {
        config: {
          bold: true,
          children: `${odEstDaysBuyer} days`,
        },
        type: 'TEXT' as 'TEXT',
      },
      header: {
        config: {
          title: 'Traditional home sale',
        },
        type: 'HEADER' as 'HEADER',
      },
      headlinePrice: {
        config: {
          value: odOfferHeadlineCents,
        },
        isEditable: true,
        onUpdateKey: 'odOfferHeadlineCents',
        type: 'CURRENCY' as 'CURRENCY',
      },
      hoaTransferFeeCents: {
        isEditable: true,
        onUpdateKey: 'hoaTransferFeeCents',
        type: 'CURRENCY' as 'CURRENCY',
      },
      homeOwnershipOverlapCosts: {
        config: {
          total: odOfferHeadlineCentsRefPoint,
        },
        isEditable: true,
        onUpdateKey: 'odHomeOwnership',
        type: 'CURRENCY_PERCENT' as 'CURRENCY_PERCENT',
      },
      mortgagePayoffCents: {
        isEditable: true,
        onUpdateKey: 'mortgagePayoffCents',
        type: 'CURRENCY' as 'CURRENCY',
      },
      otherClosingCosts: {
        config: {
          percent: +((odOtherClosingCostsCents * 100) / odOfferHeadlineCentsRefPoint).toFixed(2),
          total: odOfferHeadlineCentsRefPoint,
          value: odOtherClosingCostsCents,
        },
        type: 'CURRENCY_PERCENT' as 'CURRENCY_PERCENT',
      },
      repairsNeeded: {
        config: {
          placeholder: 'TBD',
        },
        isEditable: true,
        onUpdateKey: 'repairsNeeded',
        type: 'CURRENCY' as 'CURRENCY',
      },
      sellerConcessions: {
        config: {
          total: odOfferHeadlineCentsRefPoint,
        },
        isEditable: true,
        onUpdateKey: 'odSellerConcessions',
        type: 'CURRENCY_PERCENT' as 'CURRENCY_PERCENT',
      },
      titleEscrow: {
        config: {
          value: titleInsuranceEscrowNotaryCents,
        },
        type: 'CURRENCY' as 'CURRENCY',
      },
      total: {
        config: {
          bolder: true,
          value: odTotalCents,
        },
        type: 'CURRENCY' as 'CURRENCY',
      },
    };

    const calculatorColumns = [brokerageColumn, odColumn];

    return {
      brokerageInfo,
      calculatorColumns,
      calculatorFields,
      calculatorState,
      hasDeclineReason,
      isAccepted,
      isExpired,
      isShowingDeclineModal,
      streetAddress,
    };
  },
);

const mapDispatchToProps = {
  beginOfferAccept: brokerageOfferFlowActions.beginOfferAccept,
  hideDeclineOfferModal: brokerageOfferFlowActions.hideDeclineOfferModal,
  onDeclineSubmit: brokerageOfferFlowActions.onDeclineSubmit,
  onUpdate: (key, value) => brokerageOfferFlowActions.updateBrokerageOfferDetails({ [key]: value }),
  redirectToComplete: brokerageOfferFlowActions.redirectToComplete,
  showDeclineOfferModal: brokerageOfferFlowActions.showDeclineOfferModal,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(OfferDetailsPage);
